// frontend/src/components/admin/EventsCard.tsx
//
// Product-events panel. Reads GET /admin/analytics/events (analytics_events
// aggregates) and renders top events (count / sessions / signed-in users) and a
// by-day trend. Companion to WebTrafficCard; same self-hosted, cookieless,
// bots-excluded, consent-gated source.
import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Alert,
  Button, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Divider,
} from '@mui/material';
import { Refresh, BarChart as BarChartIcon } from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { api } from '../../utils/api';

const TEAL = '#2D6E6A';

interface EventStats {
  success: boolean;
  rangeDays: number;
  topEvents: { event_name: string; count: number; sessions: number; known_users: number }[];
  byDay: { day: string; events: number }[];
}

const EventsCard: React.FC = () => {
  const [data, setData] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = async (rangeDays: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/admin/analytics/events?days=${rangeDays}`);
      const result = await response.json();
      if (response.ok && result.success) {
        setData(result);
      } else {
        throw new Error(result.error || 'Failed to load events');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const totalEvents = data ? data.topEvents.reduce((s, e) => s + e.count, 0) : 0;

  return (
    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon sx={{ color: TEAL }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Product events</Typography>
            <Typography variant="caption" color="text.secondary">(self-hosted, consent-gated; bots excluded)</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={days} onChange={(_e, v) => { if (v) setDays(v); }}>
              <ToggleButton value={7}>7d</ToggleButton>
              <ToggleButton value={30}>30d</ToggleButton>
              <ToggleButton value={90}>90d</ToggleButton>
            </ToggleButtonGroup>
            <Button startIcon={<Refresh />} onClick={() => load(days)} variant="outlined" size="small">Refresh</Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && !data ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={20} /><Typography variant="body2">Loading events...</Typography>
          </Box>
        ) : data ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}><Card variant="outlined" sx={{ borderRadius: 2 }}><CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Total events</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalEvents}</Typography>
                <Typography variant="caption" color="text.secondary">last {data.rangeDays}d</Typography>
              </CardContent></Card></Grid>
              <Grid item xs={6}><Card variant="outlined" sx={{ borderRadius: 2 }}><CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Distinct event types</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{data.topEvents.length}</Typography>
              </CardContent></Card></Grid>
            </Grid>

            {data.byDay.length > 0 && (
              <Box sx={{ width: '100%', height: 220, mb: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.byDay} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="evCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="events" stroke={TEAL} strokeWidth={2} fill="url(#evCount)" name="Events" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Top events</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Sessions</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Signed-in</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.topEvents.map((e) => (
                  <TableRow key={e.event_name}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{e.event_name}</TableCell>
                    <TableCell align="right">{e.count}</TableCell>
                    <TableCell align="right">{e.sessions}</TableCell>
                    <TableCell align="right">{e.known_users}</TableCell>
                  </TableRow>
                ))}
                {data.topEvents.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No events yet</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default EventsCard;
